/**
 * EmployeeAvatar — Avatar réutilisable pour les employés.
 * Affiche la photo de profil si disponible, sinon les initiales colorées.
 */
import { useState } from 'react';
import { urlFichier } from '@/lib/fichiers';

const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';

const COLORS = [
  'bg-blue-500', 'bg-emerald-500', 'bg-violet-500', 'bg-amber-500',
  'bg-rose-500', 'bg-cyan-500', 'bg-orange-500', 'bg-teal-500',
];

function getColor(name = '') {
  let sum = 0;
  for (const c of name) sum += c.charCodeAt(0);
  return COLORS[sum % COLORS.length];
}

function getInitials(prenom = '', nom = '') {
  return `${(prenom[0] || '').toUpperCase()}${(nom[0] || '').toUpperCase()}`;
}

const SIZE_MAP = {
  xs: 'w-7 h-7 text-xs',
  sm: 'w-9 h-9 text-xs',
  md: 'w-11 h-11 text-sm',
  lg: 'w-14 h-14 text-base',
  xl: 'w-20 h-20 text-lg',
};

function buildPhotoUrl(photoUrl) {
  if (!photoUrl) return null;
  // data: URLs (base64 inline) — already complete
  if (photoUrl.startsWith('data:')) return photoUrl;
  // absolute HTTP URLs
  if (photoUrl.startsWith('http')) return photoUrl;
  // Normaliser les anciens chemins sans /api/ (ex: /uploads/...) → /api/uploads/...
  if (photoUrl.startsWith('/uploads/')) {
    photoUrl = `/api${photoUrl}`;
  }
  // relative server paths (e.g. /api/uploads/profils/...)
  return urlFichier(`${API_URL}${photoUrl}`);
}

export function EmployeeAvatar({ photoUrl, prenom = '', nom = '', size = 'md', className = '' }) {
  const [imgFailed, setImgFailed] = useState(false);
  const initials = getInitials(prenom, nom);
  const colorClass = getColor(`${prenom}${nom}`);
  const sizeClass = SIZE_MAP[size] || SIZE_MAP.md;
  const fullUrl = buildPhotoUrl(photoUrl);

  const handleImageLoad = (e) => {
    // Rejeter les images 1x1 ou très petites (fichiers corrompus)
    if (e.target.naturalWidth < 10 || e.target.naturalHeight < 10) {
      setImgFailed(true);
    }
  };

  // Si pas de photo, ou si l'image a échoué à charger
  if (!fullUrl || imgFailed) {
    return (
      <div
        className={`${sizeClass} ${colorClass} rounded-full flex items-center justify-center shrink-0 font-semibold text-white select-none ${className}`}
        data-testid="employee-avatar-initials"
        title={`${prenom} ${nom}`}
      >
        {initials || '?'}
      </div>
    );
  }

  return (
    <img
      src={fullUrl}
      alt={`${prenom} ${nom}`}
      className={`${sizeClass} rounded-full object-cover shrink-0 ring-2 ring-white ${className}`}
      data-testid="employee-avatar-photo"
      onLoad={handleImageLoad}
      onError={() => setImgFailed(true)}
    />
  );
}
